import PropTypes from "prop-types";
import React, { useEffect, useState, useCallback } from "react";
import { FocusTrap } from "focus-trap-react";
import { useNavigate } from "react-router-dom";
import { connect } from "react-redux";
import { RemoveScrollBar } from "react-remove-scroll-bar";
import { ReactComponent as Close } from "../../../assets/images/close-concierge.svg";
import { ReactComponent as ChatConciergeIcon } from "../../../assets/images/gradient/rose-gradient.svg";
import { ReactComponent as SupportIcon } from "../../../assets/images/snow-chat/support-icon.svg";
import LandingChat from "./LandingChat";
import { hideConciergeChat } from "../../../reducers/global/GlobalActions";
import RouteConstants from "../../../utils/RouteConstants";

function ConciergePopup({
  show,
  hidePopup,
  isPopup,
  isContactSupport,
  isMyCssContactSupport,
  askRoseProductSlug,
}) {
  const [isLoading, setIsLoading] = useState(false);
  const [focusTrapActive, setFocusTrapActive] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!show) {
      setFocusTrapActive(false);
      return () => {}; // Return empty cleanup function
    }

    const chat = document.getElementById("concierge-popup-chat");
    if (chat) {
      setTimeout(() => {
        chat.style.visibility = "visible";
        // Activate focus trap after visibility is set
        setTimeout(() => {
          setFocusTrapActive(true);
        }, 50);
      }, 100);
    }
    window.onpopstate = () => {
      hidePopup();
    };
    if (window.location.pathname === RouteConstants.PRODUCT_PAGE_URL + askRoseProductSlug) {
      hidePopup();
    }
    const handleClosing = (e) => {
      const chatElement = document.getElementById("concierge-popup-chat");
      if (chatElement && !chatElement.contains(e.target) && show) {
        hidePopup();
      }
    };

    setTimeout(() => {
      document.addEventListener("click", handleClosing);
    }, 200);

    return () => {
      document.removeEventListener("click", handleClosing);
    };
  }, [show, hidePopup, askRoseProductSlug]);

  useEffect(() => {
    if (!isPopup && window.innerWidth > 991) {
      if (!isContactSupport && !isMyCssContactSupport) {
        navigate("/");
        return;
      }
    }

    if (show && isPopup && window.innerWidth <= 991) {
      navigate("/concierge-popup");
      setIsLoading(true);
    }
  }, [isPopup, show, isContactSupport, isMyCssContactSupport, navigate]);

  const closePopup = useCallback(
    (bookMeetingClick) => {
      setFocusTrapActive(false);
      if (!isPopup && !bookMeetingClick) navigate(-1);
      hidePopup();
    },
    [isPopup, navigate, hidePopup],
  ); // Add dependencies that closePopup uses

  const closePopupKeyBoardHandler = useCallback(
    (e) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        closePopup(false);
      }
    },
    [closePopup],
  ); // Now closePopup is stable, so this won't recreate unnecessarily

  // Add ESC key handler - Even clearer version
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === "Escape") {
        closePopup(false);
      }
    };

    if (show && !isLoading) {
      document.addEventListener("keydown", handleEscape);
      return () => document.removeEventListener("keydown", handleEscape);
    }

    return () => {}; // Empty cleanup function
  }, [show, isLoading, closePopup]);

  return (
    show &&
    !isLoading && (
      <>
        {isPopup && <RemoveScrollBar />}
        <div
          className="concierge-wrapper"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-title"
        >
          <FocusTrap
            active={focusTrapActive}
            focusTrapOptions={{
              initialFocus: false,
              allowOutsideClick: true,
            }}
          >
            <div className="concierge-popup" id="concierge-popup-chat">
              <div className="conciergeChatContent-close">
                <Close
                  height="14"
                  width="14"
                  className="close cursor-pointer"
                  onClick={() => closePopup(false)}
                  role="button"
                  tabIndex={0}
                  aria-label="Close Ask Rose Popup"
                  onKeyDown={closePopupKeyBoardHandler}
                />
                <div className="icon-text-holder">
                  {isContactSupport || isMyCssContactSupport ? (
                    <SupportIcon className="supportIcon mx-auto" />
                  ) : (
                    <ChatConciergeIcon className="chatConciergeIcon mx-auto" />
                  )}
                  <p className="label mb-0" id="modal-title">
                    {isContactSupport || isMyCssContactSupport ? "Support" : "Ask Rose"}
                  </p>
                </div>
              </div>
              {/* eslint-disable-next-line react/jsx-no-bind */}
              <LandingChat setShowPopup={closePopup} />
            </div>
          </FocusTrap>
        </div>
      </>
    )
  );
}

ConciergePopup.defaultProps = {
  isPopup: true,
  show: false,
  isContactSupport: false,
  isMyCssContactSupport: false,
  askRoseProductSlug: "",
};

ConciergePopup.propTypes = {
  isPopup: PropTypes.bool,
  show: PropTypes.bool,
  hidePopup: PropTypes.func.isRequired,
  isContactSupport: PropTypes.bool,
  isMyCssContactSupport: PropTypes.bool,
  askRoseProductSlug: PropTypes.string,
};

const mapStateToProps = (state, ownProps) => ({
  show: ownProps.isPage || state.global.showConciergePopup,
  isContactSupport: state.global.isSupportChat,
  isMyCssContactSupport: state.global.isMyCssSupportChat,
  goBackLocation: state.global.lastLocation,
  askRoseProductSlug: state.global.askRoseProductSlug,
});

const mapActionsToProps = (dispatch) => ({
  hidePopup: () => dispatch(hideConciergeChat()),
});

export default connect(mapStateToProps, mapActionsToProps)(ConciergePopup);
